import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Flex,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import * as _ from 'lodash';
import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { finleyAssert } from '@finley-technologies/common';
import { getPartyShell } from '@finley-technologies/credit-agreement-utils';

import { Maybe } from 'types/dashboard-core-gqlTypes.generated';

import { WipConfig } from '../../types';
import { Select } from 'packages/common-frontend/components/Select';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

const partyKeyToPartyTypeMap: {
  [key in keyof CreditAgreement.DomainEntity.PartyEntities]: CreditAgreement.DomainEntity.Items.PartyType;
} = {
  adminAgents: CreditAgreement.DomainEntity.Items.PartyType.AdminAgent,
  backupServicers: CreditAgreement.DomainEntity.Items.PartyType.BackupServicer,
  borrowers: CreditAgreement.DomainEntity.Items.PartyType.Borrower,
  capitalProviders:
    CreditAgreement.DomainEntity.Items.PartyType.CapitalProvider,
  capitalProviderRepresentatives:
    CreditAgreement.DomainEntity.Items.PartyType.CapitalProviderRepresentative,
  collectionAccountBanks:
    CreditAgreement.DomainEntity.Items.PartyType.CollectionAccountBank,
  payingAgents: CreditAgreement.DomainEntity.Items.PartyType.PayingAgent,
  servicers: CreditAgreement.DomainEntity.Items.PartyType.Servicer,
  verificationAgents:
    CreditAgreement.DomainEntity.Items.PartyType.VerificationAgent,
};

export const getPartyTypeFromPartyKey = (
  partyKey: string,
): CreditAgreement.DomainEntity.Items.PartyType => {
  const partyType =
    partyKeyToPartyTypeMap[partyKey as keyof typeof partyKeyToPartyTypeMap];
  finleyAssert(partyType, `Unknown partyKey ${partyKey}`);

  return partyType;
};

type AddPartyModalProps = {
  basePropertyKey: string;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  wipConfig: WipConfig;
};
export const AddPartyModal = ({
  basePropertyKey,
  isOpen,
  setIsOpen,
  wipConfig: {
    formProps,
    creditAgreementFullArtifactPartyMap,
    capitalProvidersMap,
    setCapitalProvidersMap,
  },
}: AddPartyModalProps) => {
  const possibleKeys = _.keys(
    getIn(creditAgreementFullArtifactPartyMap, basePropertyKey),
  );

  const selectionOptions = _.map(possibleKeys, (key) => {
    return {
      label: _.startCase(getPartyTypeFromPartyKey(key)),
      value: key,
    };
  });

  const [selectedKey, setSelectedKey] = useState<Maybe<string>>(undefined);

  const createParty = ({
    type,
    propertyKey,
  }: {
    type: CreditAgreement.DomainEntity.Items.PartyType;
    propertyKey: string;
  }) => {
    const partyShell = getPartyShell({
      type,
      organizationId: uuidv4(),
    });
    const parties = getIn(formProps.values, basePropertyKey);
    const updatedPartySection = {
      ...parties,
      [propertyKey]: [...parties[propertyKey], partyShell],
    };
    if (type === CreditAgreement.DomainEntity.Items.PartyType.CapitalProvider) {
      setCapitalProvidersMap({
        ...capitalProvidersMap,
        [partyShell.entity.id]: partyShell.entity,
      });
    }

    formProps.setFieldValue(basePropertyKey, updatedPartySection);
    setIsOpen(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <ModalOverlay />
      <ModalContent padding="20px" minW="2xl">
        <ModalHeader>Add Party</ModalHeader>
        <ModalCloseButton />
        <ModalBody width="100%">
          <Select
            value={selectedKey}
            onChange={(propertyKey) => setSelectedKey(propertyKey)}
            options={selectionOptions}
            placeholderLabel="Select party"
          />
          <Flex justifyContent="center" mt="8">
            <Button
              onClick={() => {
                if (selectedKey) {
                  const partyType = getIn(
                    creditAgreementFullArtifactPartyMap,
                    `${basePropertyKey}.${selectedKey}`,
                  );
                  createParty({
                    type: partyType,
                    propertyKey: selectedKey,
                  });
                }
                setIsOpen(false);
              }}
              aria-label="Done"
              isDisabled={_.keys(formProps.errors).length > 0}
            >
              Done
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
